/**
 * Show State Machine
 *
 * States: idle -> loading -> syncing -> zone_select -> ready -> countdown -> playing -> completed
 *
 * Transitions are driven by schedule poll responses and internal events.
 *
 * READY is a client-only waiting phase: the phone is synced and knows its
 * sector/row, has reported 'ready', and is waiting for the operator to start
 * the countdown. The wall never enters it (it keeps showing posts until
 * countdown). The imperative driver in App.jsx sets it directly; getNextState
 * does not emit it.
 */

export const SHOW_STATES = {
  IDLE: 'idle',
  LOADING: 'loading',
  SYNCING: 'syncing',
  ZONE_SELECT: 'zone_select',
  READY: 'ready',
  COUNTDOWN: 'countdown',
  PLAYING: 'playing',
  COMPLETED: 'completed',
  ERROR: 'error',
};

/**
 * Determine next state based on current state and schedule data
 * @param {string} currentState - Current show state
 * @param {Object} schedule - Schedule response from API
 * @param {Object} context - Additional context (scriptLoaded, timeSynced, etc.)
 * @returns {{ state: string, action?: string }} Next state and optional action to trigger
 */
export function getNextState(currentState, schedule, context = {}) {
  const { scriptLoaded, timeSynced } = context;

  // Server says idle — reset to idle regardless of current state
  if (schedule.status === 'idle') {
    return { state: SHOW_STATES.IDLE };
  }

  // Server says distributing, countdown, paused or playing — show is active.
  // 'paused' is a held pre-show countdown (MM #190): still active, still shows
  // the countdown (frozen), just not advancing to playing yet.
  const serverActive = ['distributing', 'countdown', 'paused', 'playing'].includes(
    schedule.status
  );

  if (!serverActive) {
    return { state: SHOW_STATES.IDLE };
  }

  switch (currentState) {
    case SHOW_STATES.IDLE:
      // Show just started — begin loading script
      return { state: SHOW_STATES.LOADING, action: 'fetchScript' };

    case SHOW_STATES.LOADING:
      if (scriptLoaded) {
        return { state: SHOW_STATES.SYNCING, action: 'syncTime' };
      }
      // Still loading — stay in loading state
      return { state: SHOW_STATES.LOADING };

    case SHOW_STATES.SYNCING:
      if (timeSynced) {
        // Determine if we should show countdown or go straight to playing
        if (
          schedule.status === 'distributing' ||
          schedule.status === 'countdown' ||
          schedule.status === 'paused'
        ) {
          return { state: SHOW_STATES.COUNTDOWN };
        }
        return { state: SHOW_STATES.PLAYING, action: 'startEngine' };
      }
      return { state: SHOW_STATES.SYNCING };

    case SHOW_STATES.COUNTDOWN:
      if (schedule.status === 'playing') {
        return { state: SHOW_STATES.PLAYING, action: 'startEngine' };
      }
      // 'countdown' and 'paused' both stay in COUNTDOWN (paused = frozen).
      return { state: SHOW_STATES.COUNTDOWN };

    case SHOW_STATES.PLAYING:
      // Stay playing — engine handles completion internally
      return { state: SHOW_STATES.PLAYING };

    case SHOW_STATES.COMPLETED:
      // If server starts a new show, transition back to loading
      if (serverActive) {
        return { state: SHOW_STATES.LOADING, action: 'fetchScript' };
      }
      return { state: SHOW_STATES.COMPLETED };

    case SHOW_STATES.ERROR:
      // Allow recovery from error if server has new show
      if (serverActive) {
        return { state: SHOW_STATES.LOADING, action: 'fetchScript' };
      }
      return { state: SHOW_STATES.ERROR };

    default:
      return { state: SHOW_STATES.IDLE };
  }
}
